import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChartBar, TrendUp, Rocket, ArrowLeft, Warning,
  CircleNotch, Sparkle, Crown, Flask, Megaphone,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Founder-only analytics dashboard — /founder/analytics
 * Three sections: Growth (DAU/WAU/MAU + MRR), Activation funnel, Onboarding drop-off.
 */
export default function FounderAnalytics() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [window, setWindow] = useState(30);
  const [growth, setGrowth] = useState(null);
  const [activation, setActivation] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [experiments, setExperiments] = useState(null);
  const [utm, setUtm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const [g, a, o, x, u] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/founder/analytics/growth`, { params: { days: window }, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/founder/analytics/activation`, { params: { days: window }, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/founder/analytics/onboarding`, { params: { days: window }, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/experiments/results`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/founder/analytics/utm`, { params: { days: window }, withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setGrowth(g.data); setActivation(a.data); setOnboarding(o.data); setExperiments(x.data.experiments || []);
      setUtm(u.data);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    } finally { setLoading(false); }
  }, [window]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <FullScreenSpinner />;
  if (forbidden) return <ForbiddenBanner onBack={() => navigate('/dashboard')} t={t} />;

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 lg:p-10">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          data-testid="fa-back-btn"
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6"
        >
          <ArrowLeft size={16} /> Dashboard
        </button>

        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-[Outfit] font-bold flex items-center gap-3">
              <ChartBar size={30} weight="fill" className="text-[#A855F7]" />
              {t('fa.title')}
            </h1>
            <p className="text-white/60 mt-2 max-w-2xl">
              {t('fa.subtitle')}
            </p>
          </div>
          <WindowSelector value={window} onChange={setWindow} />
        </header>

        {/* Section 1 — Growth */}
        <SectionHeader icon={<TrendUp size={18} weight="fill" className="text-[#10B981]" />} title={t('fa.growth')} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <MetricCard testid="fa-dau" label="DAU" value={growth?.dau ?? 0} sub={t('fa.daily_active')} accent="text-[#10B981]" />
          <MetricCard testid="fa-wau" label="WAU" value={growth?.wau ?? 0} sub={t('fa.weekly_active')} accent="text-[#2563EB]" />
          <MetricCard testid="fa-mau" label="MAU" value={growth?.mau ?? 0} sub={t('fa.monthly_active')} accent="text-[#A855F7]" />
          <MetricCard testid="fa-stickiness" label="Stickiness" value={`${growth?.stickiness_pct ?? 0}%`} sub="DAU / MAU" accent="text-[#F59E0B]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <MetricCard testid="fa-mrr" label="MRR" value={`€${(growth?.mrr_eur ?? 0).toLocaleString()}`} sub={t('fa.mrr_sub')} accent="text-[#10B981]" big />
          <MetricCard testid="fa-arr" label="ARR" value={`€${(growth?.arr_eur ?? 0).toLocaleString()}`} sub={t('fa.arr_sub')} accent="text-[#F59E0B]" big />
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-3">{t('fa.plans')}</div>
            <div className="space-y-1 text-sm">
              <PlanRow label="Pro" count={growth?.plan_counts?.pro ?? 0} color="bg-[#A855F7]" />
              <PlanRow label="Pro Trial" count={growth?.plan_counts?.pro_trial ?? 0} color="bg-[#2563EB]" />
              <PlanRow label="Agency" count={growth?.plan_counts?.agency ?? 0} color="bg-[#F59E0B]" />
            </div>
          </div>
        </div>

        {/* Signup time-series sparkline */}
        {growth?.signups_time_series?.length > 0 && (
          <div className="mb-8 p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-white/50">{t('fa.daily_signups')}</div>
              <div className="text-sm text-white/40">
                {t('fa.total')} <span className="text-white font-semibold">{growth.signups_time_series.reduce((a, d) => a + d.count, 0)}</span>
              </div>
            </div>
            <Sparkline data={growth.signups_time_series} testid="fa-signups-sparkline" />
          </div>
        )}

        {/* Section 2 — Activation funnel */}
        <SectionHeader icon={<Rocket size={18} weight="fill" className="text-[#A855F7]" />} title={t('fa.activation_funnel')} />
        {activation?.biggest_leak && activation.biggest_leak.drop_off_pct > 30 && (
          <div className="mb-4 p-4 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/40 flex items-start gap-3" data-testid="fa-biggest-leak">
            <Warning size={18} weight="fill" className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-semibold text-[#F59E0B]">{t('fa.biggest_leak')}</span>{' '}
              <span className="text-white/90">
                {t('fa.leak_msg', { pct: activation.biggest_leak.drop_off_pct })}
                {' '}<span className="font-medium">{activation.biggest_leak.from}</span>
                {' '}{t('fa.leak_and')}
                {' '}<span className="font-medium">{activation.biggest_leak.to}</span>.
              </span>
            </div>
          </div>
        )}
        <div className="space-y-2 mb-8" data-testid="fa-funnel">
          {activation?.funnel?.map((stage, idx) => (
            <FunnelStage key={stage.stage} stage={stage} idx={idx} maxCount={activation.funnel[0].count} />
          ))}
        </div>

        {/* Section 3 — Onboarding wizard */}
        <SectionHeader icon={<Sparkle size={18} weight="fill" className="text-[#EC4899]" />} title={t('fa.onboarding_wizard')} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard testid="fa-onb-cohort" label={t('fa.cohort')} value={onboarding?.cohort_size ?? 0} sub={t('fa.last_days', { days: window })} accent="text-white" />
          <MetricCard testid="fa-onb-completion" label={t('fa.completion')} value={`${onboarding?.completion_rate_pct ?? 0}%`} sub={t('fa.completed', { count: onboarding?.completed ?? 0 })} accent="text-[#10B981]" />
          <MetricCard testid="fa-onb-skip" label={t('fa.skip_rate')} value={`${onboarding?.skip_rate_pct ?? 0}%`} sub={t('fa.skipped', { count: onboarding?.skipped ?? 0 })} accent="text-[#F59E0B]" />
          <MetricCard testid="fa-onb-started" label={t('fa.started')} value={onboarding?.step_breakdown?.[0]?.reached ?? 0} sub={t('fa.any_progress')} accent="text-[#A855F7]" />
        </div>
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-8">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-3">{t('fa.step_dropoff')}</div>
          <div className="space-y-2" data-testid="fa-step-breakdown">
            {onboarding?.step_breakdown?.map((s) => (
              <div key={s.step} className="flex items-center gap-3">
                <div className="text-xs w-14 text-white/40 font-mono">{t('fa.step', { n: s.step })}</div>
                <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#A855F7] to-[#EC4899] transition-all"
                    style={{ width: `${s.pct_of_total}%` }}
                  />
                </div>
                <div className="text-sm w-24 text-right">
                  <span className="font-mono font-semibold">{s.reached}</span>
                  <span className="text-white/40 text-xs ml-1">({s.pct_of_total}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 4 — A/B Experiments */}
        <SectionHeader icon={<Flask size={18} weight="fill" className="text-[#2563EB]" />} title={t('fa.ab_experiments')} />
        <div className="space-y-4 mb-8" data-testid="fa-experiments">
          {(experiments || []).map((exp) => (
            <div key={exp.experiment_id} className="p-4 rounded-xl bg-white/5 border border-white/10" data-testid={`fa-experiment-${exp.experiment_id}`}>
              <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
                <div className="font-mono text-sm font-semibold">{exp.experiment_id}</div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${exp.active ? 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/30' : 'text-white/40 bg-white/5 border-white/15'}`}>
                  {exp.active ? t('fa.active') : t('fa.paused')}
                </span>
              </div>
              <div className="text-xs text-white/50 mb-3">{exp.description} · {t('fa.goal')} <span className="font-mono">{exp.goal}</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {exp.variants.map((v) => (
                  <div key={v.variant} className="p-3 rounded-lg bg-black/30 border border-white/10" data-testid={`fa-variant-${exp.experiment_id}-${v.variant}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono font-semibold text-white/80">{v.variant}</span>
                      {v.lift_pct != null && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${v.lift_pct >= 0 ? 'text-[#10B981] bg-[#10B981]/10' : 'text-[#EF4444] bg-[#EF4444]/10'}`}>
                          {v.lift_pct >= 0 ? '+' : ''}{v.lift_pct}% lift
                        </span>
                      )}
                    </div>
                    <div className="flex items-end gap-4">
                      <div>
                        <div className="text-2xl font-[Outfit] font-bold">{v.conversion_rate}%</div>
                        <div className="text-[10px] text-white/40 uppercase tracking-wider">{t('fa.conversion')}</div>
                      </div>
                      <div className="text-xs text-white/50 pb-1">
                        {v.converted} / {v.exposed} <span className="text-white/30">{t('fa.exposed')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {exp.total_exposed < 30 && (
                <div className="mt-2 text-[11px] text-[#F59E0B]/80">
                  {t('fa.small_sample', { count: exp.total_exposed })}
                </div>
              )}
            </div>
          ))}
          {(!experiments || experiments.length === 0) && (
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white/40">{t('fa.no_experiments')}</div>
          )}
        </div>

        {/* Section 5 — Acquisition (UTM) */}
        <SectionHeader icon={<Megaphone size={18} weight="fill" className="text-[#F59E0B]" />} title={t('fa.utm_title')} />
        <p className="text-xs text-white/40 -mt-1 mb-4">{t('fa.utm_sub')}</p>
        {utm && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <MetricCard testid="fa-utm-visits" label={t('fa.utm_visits')} value={utm.totals?.visits ?? 0} sub={t('fa.last_days', { days: window })} accent="text-[#F59E0B]" />
            <MetricCard testid="fa-utm-unique" label={t('fa.utm_unique')} value={utm.totals?.unique_visitors ?? 0} sub="visitors" accent="text-[#2563EB]" />
            <MetricCard testid="fa-utm-signups" label={t('fa.utm_signups')} value={utm.totals?.attributed_signups ?? 0} sub="UTM" accent="text-[#10B981]" />
            <MetricCard testid="fa-utm-direct" label={t('fa.utm_direct')} value={utm.totals?.direct_signups ?? 0} sub={t('fa.utm_no_utm')} accent="text-white" />
          </div>
        )}
        {utm && utm.sources?.length > 0 ? (
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4" data-testid="fa-utm-sources">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-white/40 text-left border-b border-white/10">
                    <th className="pb-2 font-semibold">{t('fa.utm_source')}</th>
                    <th className="pb-2 font-semibold text-right">{t('fa.utm_visits')}</th>
                    <th className="pb-2 font-semibold text-right">{t('fa.utm_unique')}</th>
                    <th className="pb-2 font-semibold text-right">{t('fa.utm_signups')}</th>
                    <th className="pb-2 font-semibold text-right">{t('fa.utm_paid')}</th>
                  </tr>
                </thead>
                <tbody>
                  {utm.sources.map((s) => (
                    <tr key={s.source} className="border-b border-white/5 last:border-0" data-testid={`fa-utm-row-${s.source}`}>
                      <td className="py-2.5 font-mono text-xs">{s.source}</td>
                      <td className="py-2.5 text-right text-white/70">{s.visits}</td>
                      <td className="py-2.5 text-right text-white/50">{s.unique_visitors}</td>
                      <td className="py-2.5 text-right font-semibold text-[#10B981]">{s.signups}</td>
                      <td className="py-2.5 text-right font-semibold text-[#F59E0B]">{s.paid}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="p-4 rounded-xl bg-white/5 border border-dashed border-white/15 text-sm text-white/40 mb-4" data-testid="fa-utm-empty">
            {t('fa.utm_none')}
          </div>
        )}
        {utm && utm.campaigns?.length > 0 && (
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-8" data-testid="fa-utm-campaigns">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-3">{t('fa.utm_by_campaign')}</div>
            <div className="flex flex-wrap gap-2">
              {utm.campaigns.map((c) => (
                <span key={c.campaign} className="px-3 py-1.5 bg-black/30 border border-white/10 rounded-lg text-xs font-mono">
                  {c.campaign} · <span className="text-[#F59E0B] font-semibold">{c.visits}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────── Sub-components ──────────────────────

function SectionHeader({ icon, title }) {
  return (
    <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 mt-2">
      {icon} {title}
    </h2>
  );
}

function MetricCard({ label, value, sub, accent = 'text-white', big, testid }) {
  return (
    <div data-testid={testid} className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-1">{label}</div>
      <div className={`font-bold font-[Outfit] ${accent} ${big ? 'text-3xl' : 'text-2xl'}`}>{value}</div>
      {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function PlanRow({ label, count, color }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-white/70">{label}</span>
      <span className="ml-auto font-mono font-semibold">{count}</span>
    </div>
  );
}

function FunnelStage({ stage, idx, maxCount }) {
  const pct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
  const dropOffCritical = stage.drop_off_pct > 50;
  return (
    <div className="flex items-center gap-3" data-testid={`fa-funnel-${stage.stage}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-white/10 font-mono text-xs">
        {idx + 1}
      </div>
      <div className="flex-1 min-w-0 relative h-12 bg-white/5 rounded-lg overflow-hidden border border-white/10">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#A855F7] to-[#EC4899] opacity-80"
          style={{ width: `${pct}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-between px-3">
          <span className="text-sm font-medium">{stage.label}</span>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-mono font-semibold">{stage.count}</span>
            <span className="text-white/50 text-xs">({stage.pct_of_total}%)</span>
          </div>
        </div>
      </div>
      <div className="w-20 text-right text-xs">
        {idx > 0 && stage.drop_off_pct > 0 && (
          <span className={dropOffCritical ? 'text-[#EF4444]' : 'text-white/40'}>
            −{stage.drop_off_pct}%
          </span>
        )}
      </div>
    </div>
  );
}

function Sparkline({ data, testid }) {
  const w = 800; const h = 60;
  const max = Math.max(...data.map((d) => d.count), 1);
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const points = data.map((d, i) => `${i * step},${h - (d.count / max) * h}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" data-testid={testid}>
      <defs>
        <linearGradient id="spark" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#A855F7" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#A855F7" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={points} fill="none" stroke="#A855F7" strokeWidth="2" />
      <polygon points={`0,${h} ${points} ${w},${h}`} fill="url(#spark)" />
    </svg>
  );
}

function WindowSelector({ value, onChange }) {
  const options = [
    { d: 7, label: '7d' },
    { d: 30, label: '30d' },
    { d: 90, label: '90d' },
  ];
  return (
    <div className="flex bg-white/5 border border-white/10 rounded-lg p-1" data-testid="fa-window-selector">
      {options.map((o) => (
        <button
          key={o.d}
          onClick={() => onChange(o.d)}
          data-testid={`fa-window-${o.d}`}
          className={`px-3 py-1.5 rounded text-xs transition-all ${
            value === o.d ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function FullScreenSpinner() {
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <CircleNotch size={48} className="text-[#A855F7] animate-spin" />
    </div>
  );
}

function ForbiddenBanner({ onBack, t }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <Crown size={48} weight="fill" className="text-[#F59E0B] mx-auto mb-4" />
        <h1 className="text-2xl font-[Outfit] font-bold mb-3">{t('fa.founder_only')}</h1>
        <p className="text-white/60 mb-6">{t('fa.founder_only_body')}</p>
        <button
          onClick={onBack}
          className="px-6 py-2 rounded-lg bg-[#A855F7] hover:bg-[#9333EA] text-sm"
          data-testid="fa-forbidden-back"
        >
          {t('fa.back_dashboard')}
        </button>
      </div>
    </div>
  );
}
